import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
// import dayjs from 'dayjs'

import {
  Box,
  Button,
  Card,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
// import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Add, Delete, Edit, Visibility } from '@mui/icons-material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { SearchIcon } from 'src/v3/core/icons'
import { ComplianceRequirement } from 'src/v3/core/types'
import { dataGridSx } from '../../styles'

import {
  activeLinkedServiceChanged,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from '../../manage.slice'
import {
  fetchComplianceRequirements,
  selectComplianceRequirementsByLSID,
  selectComplianceRequirementsStatusByLSID,
} from '../../compliance.slice'
import { useLinkedServiceSelection } from '../../hooks/use-linked-service-selection'
import { useNotifications } from '../../hooks/use-notifications'
import {
  COMPLIANCE_URLS,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
  MIN_BUTTON_SIZE,
} from '../../constants'
import { deleteComplianceRequirement } from '../../utils/api'
import DeleteConfirmationModal from '../../components/modals/delete-confirmation-modal'

export default function Compliance() {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let linkedServiceID = useLinkedServiceSelection()

  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let complianceRequirements = useAppSelector(
    selectComplianceRequirementsByLSID(linkedServiceID),
  )
  let complianceRequirementsStatus = useAppSelector(
    selectComplianceRequirementsStatusByLSID(linkedServiceID),
  )

  let [nameFilter, setNameFilter] = useState<string>('')
  // Status filter hidden: it was never applied to the rows.
  // let [statusFilter, setStatusFilter] = useState<string>('All')
  // Date filters hidden while requirement dates are phased out.
  // let [dateLowerFilter, setDateLowerFilter] = useState<any>(
  //   dayjs().subtract(6, 'month'),
  // )
  // let [dateUpperFilter, setDateUpperFilter] = useState<any>(
  //   dayjs().add(6, 'month'),
  // )
  let [requirementToDelete, setRequirementToDelete] =
    useState<ComplianceRequirement | null>(null)
  let [isDeleting, setIsDeleting] = useState(false)

  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let isLoaded =
    complianceRequirementsStatus === 'fulfilled' ||
    complianceRequirementsStatus === 'rejected'

  useEffect(() => {
    if (
      linkedServiceID &&
      linkedServiceID !== 'unselected' &&
      (!complianceRequirementsStatus ||
        complianceRequirementsStatus === 'uninitialized')
    ) {
      dispatch(fetchComplianceRequirements(linkedServiceID))
    }
  }, [linkedServiceID, complianceRequirementsStatus])

  useEffect(() => {
    if (linkedServicesStatus === 'fulfilled' && linkedServices?.length === 1) {
      dispatch(activeLinkedServiceChanged(linkedServices[0]?.id))
    }
  }, [linkedServicesStatus])

  let handleDelete = async () => {
    if (!requirementToDelete) return
    setIsDeleting(true)
    await deleteComplianceRequirement(requirementToDelete.id)
      .then(() => {
        showSuccess('Compliance requirement deleted')
        if (linkedServiceID && linkedServiceID !== 'unselected') {
          dispatch(fetchComplianceRequirements(linkedServiceID))
        }
        setRequirementToDelete(null)
      })
      .catch(() => showError('Failed to delete compliance requirement'))
      .finally(() => setIsDeleting(false))
  }

  let gridRows = useMemo(
    () =>
      (complianceRequirements ?? [])
        .filter((r) => {
          if (
            nameFilter &&
            !r.name.toLowerCase().includes(nameFilter.toLowerCase())
          )
            return false
          // if (
          //   dateLowerFilter &&
          //   dayjs(r.expirationDate).isBefore(dayjs(dateLowerFilter), 'day')
          // )
          //   return false
          // if (
          //   dateUpperFilter &&
          //   dayjs(r.expirationDate).isAfter(dayjs(dateUpperFilter), 'day')
          // )
          //   return false
          return true
        })
        .map((r) => ({
          id: r.id,
          name: r.name,
          // issueDate: r.issueDate,
          // expirationDate: r.expirationDate,
          _raw: r,
        })),
    [
      complianceRequirements,
      nameFilter /* , dateLowerFilter, dateUpperFilter */,
    ],
  )

  let GridToolbar = useCallback(
    () => (
      <Box sx={{ backgroundColor: 'background.paperDark' }}>
        <DataGridToolbar
          fileName="compliance-requirements"
          showExport={false}
        />
      </Box>
    ),
    [],
  )

  let gridColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
      },
      // Date columns hidden while requirement dates are phased out.
      // {
      //   field: 'issueDate',
      //   headerName: 'Issue Date',
      //   width: 152,
      //   valueFormatter: (value: string) =>
      //     value ? dayjs(value).format('MMM D, YYYY') : '—',
      // },
      // {
      //   field: 'expirationDate',
      //   headerName: 'Expiration Date',
      //   width: 152,
      //   valueFormatter: (value: string) =>
      //     value ? dayjs(value).format('MMM D, YYYY') : '—',
      // },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 152,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: (params) => (
          <Box
            className="flex items-center justify-end"
            sx={{ height: '100%' }}
          >
            <Tooltip title="View">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(COMPLIANCE_URLS.DETAIL(params.row.id))
                }}
                aria-label={`View ${params.row.name}`}
                sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
              >
                <Visibility color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(COMPLIANCE_URLS.EDIT(params.row.id))
                }}
                aria-label={`Edit ${params.row.name}`}
                sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
              >
                <Edit color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  setRequirementToDelete(params.row._raw)
                }}
                aria-label={`Delete ${params.row.name}`}
                color="error"
                sx={{ minWidth: MIN_BUTTON_SIZE, minHeight: MIN_BUTTON_SIZE }}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [navigate],
  )

  return (
    <Container maxWidth="xl" className="py-6">
      <Box>
        <Typography variant="h4" component="h1" className="pb-6">
          Compliance
        </Typography>
        {isLoaded ? (
          <Card className="p-6 mb-6" sx={{ bgcolor: 'background.paper' }}>
            <Box className="flex flex-wrap gap-6">
              <FormControl
                sx={{
                  flex: '1 1 280px',
                  backgroundColor: 'background.paper',
                }}
              >
                <InputLabel id="linked-service-filter">
                  Linked Service
                </InputLabel>
                <Select
                  labelId="linked-service-filter"
                  label="Linked Service"
                  value={linkedServiceID || ''}
                  onChange={(event) =>
                    dispatch(activeLinkedServiceChanged(event.target.value))
                  }
                  size="medium"
                  displayEmpty
                >
                  {linkedServices.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Search Compliance Requirements"
                placeholder="Search by name"
                size="medium"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                sx={{
                  flex: '1 1 280px',
                  backgroundColor: 'background.paper',
                }}
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon className="mr-2" />,
                  },
                }}
              />

              {/* Status filter hidden: it was never applied to the rows.
              <FormControl
                sx={{ minWidth: 180, backgroundColor: 'background.paper' }}
              >
                <InputLabel id="status-filter">Status</InputLabel>
                <Select
                  labelId="status-filter"
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  size="medium"
                  displayEmpty
                >
                  <MenuItem value="All">All Statuses</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
              */}

              {/* Date filters hidden while requirement dates are phased out.
              <Box className="col-span-1 lg:col-span-3">
                <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="From"
                      value={dateLowerFilter ? dayjs(dateLowerFilter) : null}
                      onChange={setDateLowerFilter}
                      slotProps={{
                        textField: {
                          size: 'medium',
                          sx: { bgcolor: 'background.paper' },
                        },
                      }}
                    />
                    <DatePicker
                      label="To"
                      value={dateUpperFilter ? dayjs(dateUpperFilter) : null}
                      onChange={setDateUpperFilter}
                      slotProps={{
                        textField: {
                          size: 'medium',
                          sx: { bgcolor: 'background.paper' },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
              */}
            </Box>
          </Card>
        ) : (
          <Card
            className="p-6 mb-6"
            sx={{ bgcolor: 'background.paper' }}
            role="status"
            aria-busy="true"
            aria-label="Loading filters"
          >
            <Box className="flex flex-wrap gap-6" aria-hidden="true">
              <Skeleton
                variant="rectangular"
                height={56}
                sx={{ flex: '1 1 280px', borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                height={56}
                sx={{ flex: '1 1 280px', borderRadius: 1 }}
              />
            </Box>
          </Card>
        )}
        <Card className="p-6 mb-6">
          <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <Box className="flex items-center gap-2">
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                onClick={() =>
                  navigate(COMPLIANCE_URLS.CREATE, {
                    state: { linkedServiceID },
                  })
                }
                aria-label="Create new compliance requirement"
                className="mb-2 md:mb-0"
                disabled={!linkedServiceID}
              >
                Create Requirement
              </Button>
            </Box>
          </Box>
          <DataGridPremium
            rows={gridRows}
            columns={gridColumns}
            slots={{ toolbar: GridToolbar }}
            showToolbar
            initialState={{
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: DATAGRID_DEFAULT_PAGE_SIZE,
                },
              },
              pinnedColumns: { right: ['actions'] },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            pagination
            disableRowSelectionOnClick
            sx={dataGridSx}
          />
        </Card>
      </Box>
      <DeleteConfirmationModal
        isOpen={!!requirementToDelete}
        onClose={() => setRequirementToDelete(null)}
        onConfirm={handleDelete}
        entityName="compliance requirement"
        entityDisplayName={requirementToDelete?.name || ''}
        isSubmitting={isDeleting}
      />
      {NotificationComponent}
    </Container>
  )
}
